/**
 * Convert markdown template text into structured Document Editor content.
 */
export function markdownToEditorContent(markdown, options = {}) {
  const { title = 'Untitled Document', subtitle = '' } = options;
  const chapters = [];
  let currentChapter = null;
  let currentSection = null;
  let buffer = [];
  let chNum = 0;
  let secNum = 0;

  const flushSection = () => {
    if (!currentSection) return;
    const text = buffer.join('\n').trim();
    buffer = [];

    if (currentSection._listItems?.length) {
      const numbered = currentSection._listItems.some((i) => i.numbered);
      if (numbered) {
        currentSection.type = 'steps';
        currentSection.steps = currentSection._listItems.map((i) => i.text);
      } else {
        currentSection.type = 'list';
        currentSection.items = currentSection._listItems.map((i) => i.text);
      }
      delete currentSection._listItems;
    } else if (text) {
      currentSection.type = 'body';
      currentSection.body = text;
    }

    if (currentChapter && (currentSection.body || currentSection.steps || currentSection.items)) {
      currentChapter.sections.push(currentSection);
    }
    currentSection = null;
  };

  const flushChapter = () => {
    flushSection();
    if (currentChapter?.sections?.length) chapters.push(currentChapter);
    currentChapter = null;
  };

  const ensureChapter = () => {
    if (!currentChapter) {
      chNum += 1;
      secNum = 0;
      currentChapter = { id: `ch${chNum}`, title: 'Content', sections: [] };
    }
  };

  const ensureSection = (sectionTitle = 'Section') => {
    ensureChapter();
    if (!currentSection) {
      secNum += 1;
      currentSection = { id: `ch${chNum}-s${secNum}`, title: sectionTitle };
    }
  };

  for (const line of markdown.split('\n')) {
    if (line.startsWith('# ') && !line.startsWith('## ')) continue;
    if (line.startsWith('## ')) {
      flushChapter();
      chNum += 1;
      secNum = 0;
      currentChapter = { id: `ch${chNum}`, title: line.slice(3).trim(), sections: [] };
      continue;
    }
    if (line.startsWith('### ')) {
      flushSection();
      secNum += 1;
      currentSection = { id: `ch${chNum}-s${secNum}`, title: line.slice(4).trim() };
      continue;
    }
    if (line === '---') continue;

    const bullet = line.match(/^[-*] (.+)/);
    const numbered = line.match(/^\d+\. (.+)/);
    if (bullet || numbered) {
      ensureSection('Items');
      if (!currentSection._listItems) currentSection._listItems = [];
      currentSection._listItems.push({
        text: bullet ? bullet[1] : numbered[1],
        numbered: !!numbered,
      });
      continue;
    }

    ensureSection();
    buffer.push(line);
  }
  flushChapter();

  if (chapters.length === 0) {
    chapters.push({
      id: 'ch1',
      title: 'Content',
      sections: [{ id: 'ch1-s1', title: 'Body', type: 'body', body: markdown.trim() }],
    });
  }

  return {
    content: {
      meta: {
        title,
        subtitle,
        version: '1.0',
        lastUpdated: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      },
      chapters,
    },
    imageOverrides: {},
  };
}